/**
 * Tenant readiness card — a dashboard-mounted checklist of "globally
 * required" tenant settings that a fresh install hasn't filled in yet.
 *
 * The same `useTenantReadiness()` hook drives:
 *   • this <TenantReadinessCard /> on the dashboard,
 *   • the small numeric badge on the sidebar's "New here? Show me around"
 *     compass button, and
 *   • per-setting inline banners on Companies, Global Settings' letterhead
 *     tab, Warehouses, Branches, Payment Terms, Taxes and Chart of
 *     Accounts (each filters the same `steps` list down to its own code).
 *
 * The card hides itself entirely when `all_green` — mirrors the "Needs
 * attention" pattern on the dashboard where an empty exception queue
 * renders nothing rather than a smug "All clear" card.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, Rocket } from 'lucide-react';

import { api } from '@/shared/api/client';
import type { ApiEnvelope } from '@/shared/api/hooks';

import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { cn } from '@/shared/ui/utils';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

/** One row returned by `/api/v1/setup/status`. Codes are stable strings. */
export interface ReadinessStep {
  code:
    | 'company_logo'
    | 'company_tax_number'
    | 'company_address'
    | 'warehouses'
    | 'branches'
    | 'payment_terms'
    | 'taxes'
    | 'chart_of_accounts'
    | 'numbering_rules'
    | 'letterhead_style'
    | 'non_owner_users'
    // Widen for forward compatibility — new checks landing on the server
    // shouldn't need a client rebuild to render.
    | (string & {});
  label: string;
  ok: boolean;
  action_url: string;
  help: string;
}

export interface ReadinessPayload {
  steps: ReadinessStep[];
  remaining: number;
  all_green: boolean;
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

const READINESS_QUERY_KEY = ['tenant-readiness'] as const;

export function useTenantReadiness() {
  return useQuery({
    queryKey: READINESS_QUERY_KEY,
    queryFn: async (): Promise<ReadinessPayload> => {
      try {
        const { data } = await api.get<ApiEnvelope<ReadinessPayload>>(
          '/api/v1/setup/status',
        );
        return data.data;
      } catch {
        // Fresh install where the endpoint isn't reachable — return an
        // "all green" shape so no card / banner / badge renders. Better
        // to hide the whole feature than to yell at the user because
        // their token expired.
        return { steps: [], remaining: 0, all_green: true };
      }
    },
    // Server caches for 60 s per tenant; matching that here means we never
    // hit the API twice for the same paint.
    staleTime: 60_000,
    // Refetch on window focus so once the user completes a step and
    // Alt-Tabs back, the card catches up without a hard reload.
    refetchOnWindowFocus: true,
  });
}

/** Filter helper for the inline banners on each setting page. */
export function useReadinessStep(code: ReadinessStep['code']) {
  const q = useTenantReadiness();
  const step = q.data?.steps.find((s) => s.code === code) ?? null;
  return { step, isLoading: q.isLoading };
}

/* -------------------------------------------------------------------------- */
/* Progress bar                                                               */
/* -------------------------------------------------------------------------- */

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {done} of {total} complete
        </span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label="Setup completion"
      >
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-500 dark:bg-emerald-400"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Card — mounted at the top of DashboardPage                                 */
/* -------------------------------------------------------------------------- */

export function TenantReadinessCard() {
  const { data, isLoading } = useTenantReadiness();

  // While the first load is in flight OR the tenant is fully green,
  // render nothing — this is the noise-suppression the plan calls for.
  // Loading skeleton would flash-and-hide on every dashboard mount.
  if (isLoading || !data || data.all_green) return null;

  const total = data.steps.length;
  const done = total - data.remaining;
  const incomplete = data.steps.filter((s) => !s.ok);

  return (
    <Card className="border-amber-500/40 bg-amber-500/[0.03] dark:border-amber-500/30 dark:bg-amber-500/[0.04]">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Rocket className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            Complete your setup
            <Badge variant="warning" size="sm">
              {data.remaining}
            </Badge>
          </CardTitle>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          A few globally-required settings are still empty. Fixing them once
          unlocks every document workflow across the app.
        </p>
        <div className="mt-3">
          <ProgressBar done={done} total={total} />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {incomplete.map((step) => (
          <Alert
            key={step.code}
            variant="warning"
            title={step.label}
            description={
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>{step.help}</span>
                <Link
                  to={step.action_url}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 self-start rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors',
                    'hover:bg-amber-500/20 dark:text-amber-200',
                  )}
                >
                  Fix it
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            }
          />
        ))}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Inline banner — mounted on individual settings pages                       */
/* -------------------------------------------------------------------------- */

export interface ReadinessInlineBannerProps {
  /**
   * Which step to check. May be a single code (banner shows when that one
   * step is incomplete) or a list (banner shows when any of them is
   * incomplete — used on CompaniesPage where the three company-profile
   * fields share one banner).
   */
  code: ReadinessStep['code'] | ReadinessStep['code'][];
  /** Override the banner title. Defaults to the step's own label. */
  title?: string;
  /** Override the banner description. Defaults to the step's own help copy. */
  description?: string;
  className?: string;
}

/**
 * Inline `<Alert>` shown at the top of a specific settings page when its
 * corresponding readiness step is still incomplete. Renders nothing when
 * the step is complete or unknown.
 */
export function ReadinessInlineBanner({
  code,
  title,
  description,
  className,
}: ReadinessInlineBannerProps) {
  const codes = useMemo(() => (Array.isArray(code) ? code : [code]), [code]);
  const { data, isLoading } = useTenantReadiness();
  const incomplete = useMemo(() => {
    if (!data) return [];
    return data.steps.filter((s) => codes.includes(s.code) && !s.ok);
  }, [data, codes]);

  if (isLoading || incomplete.length === 0) return null;

  // When more than one step is incomplete (Companies page bundles three),
  // collapse them into a bullet list under a single generic title.
  if (incomplete.length > 1) {
    return (
      <Alert
        variant="warning"
        title={title ?? 'A few required company fields are still empty'}
        className={className}
      >
        <AlertDescription>
          {description ? <p className="mb-2">{description}</p> : null}
          <ul className="ml-4 list-disc space-y-0.5">
            {incomplete.map((s) => (
              <li key={s.code}>
                <span className="font-medium">{s.label}</span>
                <span className="text-muted-foreground"> — {s.help}</span>
              </li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>
    );
  }

  const step = incomplete[0];
  return (
    <Alert
      variant="warning"
      title={title ?? step.label}
      description={description ?? step.help}
      className={className}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Green-check confirmation (unused — kept for a future "All setup" ribbon)   */
/* -------------------------------------------------------------------------- */

export function ReadinessAllGoodBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="h-3.5 w-3.5" />
      All setup complete
    </span>
  );
}
